import { endOfDay, startOfDay } from 'date-fns';
import { useGoals } from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import { useTags } from 'features/tags';
import mergeWith from 'lodash/mergeWith';
import { useMemo } from 'react';
import { useToday } from 'shared/contexts/today';
import {
  useRelationships,
  useRelationshipsMap,
} from 'shared/hooks/use-relationships';
import { useUser } from 'shared/hooks/use-user';
import { TaskFilterOptions } from 'shared/types/filter';
import {
  FilterDocument,
  FilterDocumentDateFilterTypes,
  FilterDocumentDurationTypes,
  FilterDocumentFilters,
} from 'shared/types/filter-document';
import { Goal } from 'shared/types/goal';
import { ID, IDPrefixes } from 'shared/types/id';
import { goalOtherID, lifeAreaOtherID } from 'shared/types/orderable-section';
import { calculateDeadlinePresetDates } from 'shared/utils/filters/calculate-deadline-preset-dates';
import { getCommonUIDs } from 'shared/utils/get-common-uids';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';
import {
  createPrefixId,
  getPrefixedId,
  getPrefixedIdPrefix,
} from 'shared/utils/prefix-id';

const nonExistingId = 'non-existing-id';

const getGoalChildIdsRecursively = (goal: Goal, goals: Goal[]): ID[] => {
  const childGoals = goals.filter(
    (_goal) => _goal.parentIds?.[_goal.parentIds?.length - 1] === goal.id,
  );
  return childGoals.reduce<ID[]>(
    (acc, childGoal) => [
      ...acc,
      childGoal.id,
      ...getGoalChildIdsRecursively(childGoal, goals),
    ],
    [],
  );
};

export type Options = {
  uncompletedOnly?: boolean;
  includeTaskWithAction?: boolean;
  excludeHiddenTodoHabits?: boolean;
  includeArchivedGoals?: boolean;
};

export const useFilterDocumentFilters = (
  filterDocument: FilterDocument,
  {
    uncompletedOnly,
    includeTaskWithAction,
    excludeHiddenTodoHabits,
    includeArchivedGoals = false,
  }: Options,
) => {
  const today = useToday();
  const goals = useGoals();
  const lifeAreas = useLocalizedLifeAreas();
  const user = useUser();
  const relationships = useRelationships();
  const relationshipsMap = useRelationshipsMap(relationships);
  const tags = useTags();
  const weekStartsOn = getWeekStartsOn(user);

  const tagIdsSet = useMemo(() => new Set(tags.map((tag) => tag.id)), [tags]);

  const lifeAreaIdsSet = useMemo(
    () => new Set(lifeAreas.map((la) => la.id)),
    [lifeAreas],
  );

  const goalIdsSet = useMemo(() => new Set(goals.map((g) => g.id)), [goals]);

  return useMemo<TaskFilterOptions[]>(() => {
    const archivedGoalIds = includeArchivedGoals
      ? undefined
      : goals.filter((goal) => goal.archivedAt).map((goal) => goal.id);

    // Helper to calculate a single set of filter options
    const calculateOptions = (
      fdFilters: Partial<FilterDocumentFilters>,
    ): TaskFilterOptions => {
      // life area filter
      const lifeAreaFilter = fdFilters?.lifeArea;
      const filteredLifeAreaIds = lifeAreaFilter?.include
        ?.filter((id: ID) => id !== lifeAreaOtherID && lifeAreaIdsSet.has(id))
        ?.sort();

      const lifeAreaIds = lifeAreaFilter?.include?.length
        ? filteredLifeAreaIds?.length
          ? filteredLifeAreaIds
          : undefined
        : lifeAreaFilter?.include;

      const noLifeArea = lifeAreaFilter?.include?.includes(lifeAreaOtherID);

      // get all goals from the selected lifeArea's
      const lifeAreaGoalIds = lifeAreaIds?.length
        ? lifeAreaIds
            .map((lifeAreaId: ID) => {
              const _goals = goals.filter(
                (goal) => goal.lifeAreaId === lifeAreaId,
              );
              return _goals
                .map((goal) => [
                  goal.id,
                  ...getGoalChildIdsRecursively(goal, goals),
                ])
                .flat();
            })
            .flat()
            .sort()
        : undefined;

      // active goal filter
      const activeGoalFilter = fdFilters?.goalActive;
      const { start: activeGoalStart, end: activeGoalEnd } =
        activeGoalFilter?.type === FilterDocumentDateFilterTypes.Custom
          ? {
              start: activeGoalFilter?.custom?.start
                ? startOfDay(activeGoalFilter?.custom?.start)
                : undefined,
              end: activeGoalFilter?.custom?.end
                ? startOfDay(activeGoalFilter?.custom?.end)
                : undefined,
            }
          : activeGoalFilter
            ? calculateDeadlinePresetDates(activeGoalFilter.type, {
                today,
                weekStartsOn,
              }) ?? { start: undefined, end: undefined }
            : { start: undefined, end: undefined };
      const activeGoalIds =
        activeGoalStart && activeGoalEnd
          ? goals
              .filter(
                (goal) =>
                  goal.startDate &&
                  goal.deadline &&
                  goal.startDate <= activeGoalEnd &&
                  goal.deadline >= activeGoalStart,
              )
              .map((goal) => goal.id)
          : undefined;

      // goal filter
      const goalFilter = fdFilters?.goal;
      // filtering non-existing goals from the include list
      const filteredGoalIds = goalFilter?.include
        ?.filter((id: ID) => id !== goalOtherID && goalIdsSet.has(id))
        ?.toSorted();
      const goalIds = goalFilter?.include?.length
        ? filteredGoalIds?.length
          ? filteredGoalIds
          : undefined
        : undefined;

      const noGoal = goalFilter?.include?.includes(goalOtherID) ?? false;

      const finalGoalIds = getCommonUIDs(
        goalIds,
        lifeAreaGoalIds,
        activeGoalIds,
      );

      // Get task IDs from selected tags (OR logic - union of all task IDs)
      const tagFilter = fdFilters?.tag;
      // filter tag ids to only have existing tagIds
      const filteredTagIds = tagFilter?.include?.filter((tagId) =>
        tagIdsSet.has(tagId),
      );
      const tagIds = tagFilter?.include?.length
        ? filteredTagIds?.length
          ? filteredTagIds
          : null
        : tagFilter?.include;
      const noTag = tagFilter?.noTag;

      // Calculate tasks from selected tags (AND logic - intersection of task IDs)
      const taskIdsFromTagsSet = tagIds?.length
        ? tagIds.reduce<Set<ID> | undefined>((acc, tagId) => {
            const currentTagTaskIds = new Set<ID>();
            relationshipsMap[createPrefixId(tagId, IDPrefixes.Tag)]?.forEach(
              (prefixedId) => {
                if (getPrefixedIdPrefix(prefixedId) === IDPrefixes.Task) {
                  currentTagTaskIds.add(getPrefixedId(prefixedId));
                }
              },
            );
            return !acc
              ? currentTagTaskIds
              : new Set([...acc].filter((id) => currentTagTaskIds.has(id)));
          }, undefined)
        : undefined;

      // If noTag is true AND other tags are selected -> Intersection is empty (Task cannot have a tag and NO tag)
      // If only noTag is true -> We exclude tasks with any tags (logic in excludedTaskIds matches this)
      const isImpossibleCombination = noTag && tagIds?.length;

      const taskIdsFromTags =
        !isImpossibleCombination && taskIdsFromTagsSet
          ? Array.from(taskIdsFromTagsSet)
          : undefined;

      const excludedTaskIds = noTag
        ? Array.from(
            Object.keys(relationshipsMap).reduce<Set<ID>>((acc, key) => {
              if (getPrefixedIdPrefix(key) === IDPrefixes.Tag) {
                relationshipsMap[key]?.forEach((prefixedId) => {
                  const id = getPrefixedId(prefixedId);
                  // if there are more tags selected besides noTag, we need to exclude those tasks from the exclude list
                  if (
                    getPrefixedIdPrefix(prefixedId) === IDPrefixes.Task &&
                    !taskIdsFromTagsSet?.has(id)
                  ) {
                    acc.add(id);
                  }
                });
              }
              return acc;
            }, new Set<ID>()),
          )
        : undefined;

      // Extract duration filter and convert minutes to milliseconds
      const durationFilter = fdFilters?.duration;
      const minDuration = durationFilter?.more?.value
        ? durationFilter.more.value *
          (durationFilter.more.type === FilterDocumentDurationTypes.Minutes
            ? 1
            : 60) *
          60 *
          1000
        : undefined;
      const maxDuration = durationFilter?.less?.value
        ? durationFilter.less.value *
          (durationFilter.less.type === FilterDocumentDurationTypes.Minutes
            ? 1
            : 60) *
          60 *
          1000
        : undefined;

      // Extract deadline filter and convert to backend parameters
      const deadlineDeadlineFilter = fdFilters?.deadline;
      const isCustomDeadlineDeadline =
        deadlineDeadlineFilter?.type === FilterDocumentDateFilterTypes.Custom;
      const presetDeadlineDeadlineDates =
        deadlineDeadlineFilter && !isCustomDeadlineDeadline
          ? calculateDeadlinePresetDates(deadlineDeadlineFilter.type, {
              today,
              weekStartsOn,
            })
          : undefined;
      const deadlineDeadlineStartDate =
        deadlineDeadlineFilter?.type === FilterDocumentDateFilterTypes.Custom &&
        deadlineDeadlineFilter.custom?.start
          ? startOfDay(deadlineDeadlineFilter.custom.start)
          : presetDeadlineDeadlineDates?.start;
      const deadlineDeadlineEndDate =
        deadlineDeadlineFilter?.type === FilterDocumentDateFilterTypes.Custom &&
        deadlineDeadlineFilter.custom?.end
          ? endOfDay(deadlineDeadlineFilter.custom.end)
          : presetDeadlineDeadlineDates?.end;

      const deadlineFilter = fdFilters?.date;
      const isCustomDeadline =
        deadlineFilter?.type === FilterDocumentDateFilterTypes.Custom;
      const presetDeadlineDates =
        deadlineFilter && !isCustomDeadline
          ? calculateDeadlinePresetDates(deadlineFilter.type, {
              today,
              weekStartsOn,
            })
          : undefined;
      const deadlineStartDate =
        deadlineFilter?.type === FilterDocumentDateFilterTypes.Custom &&
        deadlineFilter.custom?.start
          ? startOfDay(deadlineFilter.custom.start)
          : presetDeadlineDates?.start;
      const deadlineEndDate =
        deadlineFilter?.type === FilterDocumentDateFilterTypes.Custom &&
        deadlineFilter.custom?.end
          ? endOfDay(deadlineFilter.custom.end)
          : presetDeadlineDates?.end;

      return {
        // if the array is empty, this means that the filter shouldn't match any task
        // if we would return undefined, it would mean that we don't filter on lifeArea
        // so we return a dummy id to make sure that no task is matched
        lifeAreas: lifeAreaIds?.length
          ? // when specifically filtering on goals, the life-area tasks are not allowed anymore
            goalIds?.length
            ? [nonExistingId]
            : lifeAreaIds
          : lifeAreaIds && !noLifeArea
            ? [nonExistingId]
            : undefined,
        // if the array is empty, this means that the filter shouldn't match any task
        // if we would return undefined, it would mean that we don't filter on goal
        // so we return a dummy id to make sure that no task is matched
        goals: finalGoalIds?.length
          ? finalGoalIds
          : finalGoalIds && !noGoal
            ? [nonExistingId]
            : undefined,
        types: fdFilters?.type?.include?.toSorted() ?? undefined,
        priorities: fdFilters?.priority?.include?.toSorted() ?? undefined,
        ids: taskIdsFromTags,
        excludeIds: excludedTaskIds,
        minDuration,
        maxDuration,
        noDeadlineDeadline:
          deadlineDeadlineFilter?.type ===
          FilterDocumentDateFilterTypes.NoDeadline,
        hasDeadlineDeadline:
          deadlineDeadlineFilter?.type ===
          FilterDocumentDateFilterTypes.HasDeadline,
        noDeadline:
          deadlineFilter?.type === FilterDocumentDateFilterTypes.NoDeadline,
        hasDeadline:
          deadlineFilter?.type === FilterDocumentDateFilterTypes.HasDeadline,
        deadlineDeadlineStartDate,
        deadlineDeadlineEndDate,
        deadlineStartDate,
        deadlineEndDate,
        noLifeArea: noLifeArea ? true : undefined,
        noGoal: noGoal ? true : undefined,
        showCompleted:
          filterDocument.showCompleted ??
          (uncompletedOnly === undefined ? false : !uncompletedOnly),
        includeTaskWithAction,
        excludeHiddenTodoHabits,
        archivedGoalIds,
      };
    };

    const baseOptions = calculateOptions(filterDocument.filters ?? {});

    if (filterDocument.or?.length) {
      return filterDocument.or.map((orFilters) => {
        const orOptions = calculateOptions(orFilters);
        return mergeWith({}, baseOptions, orOptions, (objValue, srcValue) =>
          srcValue !== undefined ? srcValue : objValue,
        );
      });
    }

    return [baseOptions];
  }, [
    includeArchivedGoals,
    goals,
    filterDocument.filters,
    filterDocument.or,
    filterDocument.showCompleted,
    today,
    weekStartsOn,
    relationshipsMap,
    uncompletedOnly,
    includeTaskWithAction,
    excludeHiddenTodoHabits,
    lifeAreaIdsSet,
    goalIdsSet,
    tagIdsSet,
  ]);
};
